import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AI_CHAT_MODES, CHAT_PROMT_ROLES } from '@shared/constants/ai-consts';
import { getAiChatMessageKey, uniqAiChatMessages } from './message-helpers';
import { AiTemplateChatModeType, IAiChatDetails, IAiChatJob, IAiChatListItem, IAiChatMessage, IAiChatPagination } from './types';

interface AiChatState {
    chat: IAiChatListItem | null;
    mode: AiTemplateChatModeType | null;
    job: IAiChatJob | null;
    messages: IAiChatMessage[];
    messagesPagination: IAiChatPagination | null;
}

const aiChatInitialState: AiChatState = {
    chat: null,
    job: null,
    mode: null,
    messages: [],
    messagesPagination: null,
};

const aiChatSlice = createSlice({
    name: 'aiChat',
    initialState: aiChatInitialState,
    selectors: {
        getChat: (state: AiChatState) => state.chat,
        getChatId: (state: AiChatState) => state.chat?.id,
        getChatMode: (state: AiChatState) => state.mode,
        getActiveJob: (state: AiChatState) => state.job,
        getAllMessages: (state: AiChatState) => state.messages,
        getMessagesPagination: (state: AiChatState) => state.messagesPagination,
        getMessageById: (state: AiChatState, messageId: number) => state.messages.find((message) => (message.id ?? message.clientId) === messageId),
        getUserMessages: createSelector(
            (state: AiChatState) => state.messages,
            (messages) => messages.filter((message) => message.role === CHAT_PROMT_ROLES.USER),
        ),
        getLastUserMessage: (state: AiChatState) => {
            for (let i = state.messages.length - 1; i >= 0; i -= 1) {
                const message = state.messages[i];

                if (message.role === CHAT_PROMT_ROLES.USER) {
                    return message;
                }
            }

            return null;
        },
    },
    reducers: {
        setChat: (state: AiChatState, { payload: chat }: PayloadAction<IAiChatListItem>) => {
            state.chat = chat;
        },
        resetChat: (state: AiChatState) => {
            state.chat = null;
            state.messages = [];
            state.messagesPagination = null;
            state.job = null;
        },
        setMessages: (state: AiChatState, { payload: messages }: PayloadAction<IAiChatMessage[]>) => {
            state.messages = uniqAiChatMessages(messages);
        },
        clearMessages: (state: AiChatState) => {
            state.messages = [];
            state.messagesPagination = null;
        },
        addMessage: (state: AiChatState, { payload: message }: PayloadAction<IAiChatMessage>) => {
            const messageKey = getAiChatMessageKey(message);
            const messageIndex = state.messages.findIndex((msg) => getAiChatMessageKey(msg) === messageKey);

            if (messageIndex === -1) {
                state.messages.push(message);
            } else {
                state.messages[messageIndex] = message;
            }
        },
        updateMessage: (state: AiChatState, { payload: message }: PayloadAction<IAiChatMessage>) => {
            let hasUpdatedMessage = false;

            state.messages = state.messages.map((msg) => {
                const shouldUpdate = msg.clientId === message.clientId || (message.id !== undefined && msg.id === message.id);

                if (!shouldUpdate) {
                    return msg;
                }

                hasUpdatedMessage = true;

                return message;
            });

            if (!hasUpdatedMessage) {
                state.messages.push(message);
            }

            state.messages = uniqAiChatMessages(state.messages);
        },
        removeMessage: (state: AiChatState, { payload: messageId }: PayloadAction<number>) => {
            state.messages = state.messages.filter((message) => message.id !== messageId);
        },
        setActiveJob: (state: AiChatState, { payload: job }: PayloadAction<IAiChatJob | null>) => {
            state.job = job;
        },
        removeActiveJob: (state: AiChatState) => {
            state.job = null;
        },
        setMode: (state: AiChatState, { payload }: PayloadAction<AiTemplateChatModeType | null>) => {
            console.debug('Setting mode to:', payload);
            state.mode = payload ?? null;
        },
        initChat: (state: AiChatState, { payload }: PayloadAction<IAiChatDetails>) => {
            const { activeJob, messages, mode, messagesPagination } = payload;
            const isSameChat = state.chat?.id === payload.id;
            const isOlderMessagesPage = Boolean(messagesPagination?.previous);

            state.chat = payload;
            state.job = activeJob ?? null;

            state.messages = uniqAiChatMessages(isSameChat && isOlderMessagesPage ? [...messages, ...state.messages] : messages);

            state.messagesPagination = messagesPagination ?? null;

            if (!mode) {
                state.mode = AI_CHAT_MODES.NEWS;
            }
        },
        clearChat: (state: AiChatState) => {
            state.chat = null;
            state.messages = [];
            state.messagesPagination = null;
            state.job = null;
        },
    },
});

export const { actions: aiChatActions, selectors: aiChatSelectors } = aiChatSlice;

export default aiChatSlice.reducer;
